import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Cpu, MemoryStick, Monitor, Pause, Play } from 'lucide-react';
import WaterfallScene from '../components/WaterfallScene';
import { resourceWaterfall, waterfallAmount, type WaterfallResource } from '../data/resourceWaterfall';
import { channelColour } from '../data/waterfallIdentity';
import type { ProcessSnapshot } from '../data/processes';
import './hydro.css';

const BASIS: Record<WaterfallResource, string> = {
  cpu: 'CPU time split across apps. Boost-adjusted utility is a different reading.',
  ram: 'Apps show private RAM. Windows, shared pages and unassigned memory make up the rest.',
  gpu: 'Dedicated memory on one GPU. Shared system memory and GPU engine load are separate.',
};

function HydroDemo() {
  const [resource, setResource] = useState<WaterfallResource>('cpu');
  const [load, setLoad] = useState(58);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    let inView = false;
    const update = () => setVisible(inView && !document.hidden);
    const observer = new IntersectionObserver(entries => { inView = entries[entries.length - 1].isIntersecting; update(); }, { threshold: 0.05 });
    observer.observe(element);
    document.addEventListener('visibilitychange', update);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', update); };
  }, []);
  const snapshot: ProcessSnapshot = {
    sampledAt: '2026-09-01T12:00:00Z', intervalSeconds: 1,
    cpuBusyPercent: load, cpuUtilityPercent: null,
    memoryUsedMb: 32768 * load / 100, memoryTotalMb: 32768,
    gpuAdapter: 'Example 16 GB graphics adapter', gpuMemoryUsedMb: 16384 * load / 100, gpuMemoryTotalMb: 16384,
    processes: ['Game session', 'Browser', 'Creative tools', 'Desktop'].map((name, index) => ({
      pid: index + 100, parentPid: 0, started: String(index), name,
      cpuPercent: load * [0.57, 0.15, 0.12, 0.06][index],
      memoryMb: 32768 * load / 100 * [0.41, 0.24, 0.13, 0.05][index],
      vramMb: 16384 * load / 100 * [0.66, 0.08, 0.1, 0.03][index],
      ioMbps: null, gpuPercent: null, responding: null, threads: 8, handles: 80,
    })),
  };
  const view = resourceWaterfall(snapshot, resource);
  return <div ref={host} className={`site-hydro${paused || !visible ? ' paused' : ''}`} data-resource={resource}>
    <div className="hydro-controls">
      <div className="hydro-modes" role="group" aria-label="Example resource">
        {([{ key: 'cpu', label: 'CPU', Icon: Cpu }, { key: 'ram', label: 'RAM', Icon: MemoryStick }, { key: 'gpu', label: 'GPU memory', Icon: Monitor }] as const)
          .map(mode => <button key={mode.key} type="button" aria-pressed={resource === mode.key}
            onClick={() => { setResource(mode.key); setSelected(null); }}><mode.Icon size={15} />{mode.label}</button>)}
      </div>
      <label className="hydro-load">In use<input type="range" min="0" max="100" step="1" value={load} onChange={event => setLoad(Number(event.target.value))} /><output>{load}%</output></label>
      <button className="hydro-pause" type="button" title={paused ? 'Resume animation' : 'Pause animation'} aria-label={paused ? 'Resume animation' : 'Pause animation'} onClick={() => setPaused(!paused)}>
        {paused ? <Play size={16} /> : <Pause size={16} />}
      </button>
      <span className="hydro-demo-label">Demo data</span>
    </div>
    <div className="hydro-stage"><WaterfallScene model={view.allocation} selected={selected} onSelect={setSelected} resource={resource} capacity={view.capacity} /></div>
    <div className="hydro-allocation" aria-label="Example allocations">
      {view.allocation?.channels.map(channel => <button key={channel.id} type="button" aria-pressed={selected === channel.id}
        onMouseEnter={() => setSelected(channel.id)} onMouseLeave={() => setSelected(null)} onFocus={() => setSelected(channel.id)} onBlur={() => setSelected(null)}
        onClick={() => setSelected(selected === channel.id ? null : channel.id)}>
        <i style={{ background: channelColour(channel.id) }} /><span>{channel.name}</span><b>{waterfallAmount(channel.value, resource, view.capacity)}</b>
      </button>)}
    </div>
    <p className="hydro-basis">{BASIS[resource]}</p>
  </div>;
}

const host = document.getElementById('hydro-demo');
if (host) {
  const observer = new IntersectionObserver(entries => {
    if (!entries.some(entry => entry.isIntersecting)) return;
    observer.disconnect();
    createRoot(host).render(<HydroDemo />);
  }, { rootMargin: '200px' });
  observer.observe(host);
}